import { isAzureSupportedLanguage } from "./ttsVoiceCatalog.js";

/**
 * @typedef {"readAloud" | "speakingMode"} TTSFeature
 * @typedef {"free" | "standard" | "plus"} TTSTier
 */

/**
 * Which tiers are allowed to use Azure voices for each feature. Must match
 * server-side enforcement in POST /api/tts/synthesize.
 *
 * @param {TTSFeature} feature
 * @returns {TTSTier}
 */
export const azureRequiredTier = (feature) =>
  feature === "readAloud" ? "plus" : "standard";

/**
 * Translates Readlang's subscriptionPlan string into the simplified TTS tier.
 * `freeForever` has plus-level TTS permissions.
 *
 * @param {string | undefined | null} subscriptionPlan
 * @returns {TTSTier}
 */
export const tierFromSubscriptionPlan = (subscriptionPlan) => {
  if (subscriptionPlan === "plus" || subscriptionPlan === "freeForever") {
    return "plus";
  }
  if (subscriptionPlan === "standard") return "standard";
  return "free";
};

/**
 * @param {TTSTier} userTier
 * @param {TTSTier} requiredTier
 * @returns {boolean}
 */
export const tierMeetsRequirement = (userTier, requiredTier) => {
  if (requiredTier === "free") return true;
  if (requiredTier === "standard") {
    return userTier === "standard" || userTier === "plus";
  }
  return userTier === "plus";
};

/**
 * @param {string | undefined | null} subscriptionPlan
 * @param {TTSFeature} feature
 * @returns {boolean}
 */
export const planCanUseAzureFeature = (subscriptionPlan, feature) =>
  tierMeetsRequirement(
    tierFromSubscriptionPlan(subscriptionPlan),
    azureRequiredTier(feature)
  );

/**
 * True if Azure synthesis is available for this plan/feature/language.
 *
 * @param {string | undefined | null} subscriptionPlan
 * @param {TTSFeature} feature
 * @param {string} language
 * @returns {boolean}
 */
export const planCanUseAzure = (subscriptionPlan, feature, language) =>
  isAzureSupportedLanguage(language) &&
  planCanUseAzureFeature(subscriptionPlan, feature);
